import React, { useState } from 'react';
import { Cog, Building, UserCog, Tag, MapPin, Smartphone, AlertTriangle, X, Link } from 'lucide-react';

// Tab Components
const TabButton = ({ active, icon, label, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
        active 
          ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700' 
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
      }`}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </button>
  );
};

// Modal Component
const Modal = ({ title, children, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium">{title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

// Access Permissions Modal Component
const AccessPermissionsModal = ({ isOpen, onClose, user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [accessData, setAccessData] = useState({
    screens: {
      dashboard: true,
      reviews: true,
      analytics: true,
      settings: false
    },
    actions: {
      view: true,
      edit: false,
      delete: false,
      export: true
    },
    products: [],
    locations: [],
    apps: []
  });
  
  const products = [
    { id: 1, name: 'Product Original', category: 'Water Purifiers' },
    { id: 2, name: 'Product Premium', category: 'Water Purifiers' },
    { id: 3, name: 'Product Max', category: 'Water Purifiers' },
    { id: 4, name: 'Product Everyday', category: 'Water Filters' },
    { id: 5, name: 'Product Complete', category: 'Water Solutions' }
  ];
  
  const locations = [
    { id: 1, name: 'Delhi Showroom', region: 'North' },
    { id: 2, name: 'Mumbai Central', region: 'West' },
    { id: 3, name: 'Bangalore Tech Park', region: 'South' },
    { id: 4, name: 'Kolkata East End', region: 'East' },
    { id: 5, name: 'Chennai Beachside', region: 'South' }
  ];
  
  const apps = [
    { id: 1, name: 'Product Mobile App', platform: 'iOS & Android' },
    { id: 2, name: 'Product Service Portal', platform: 'Web' },
    { id: 3, name: 'Product Agent App', platform: 'Android' }
  ];
  
  const handleCheckboxChange = (category, item) => {
    setAccessData(prev => {
      const updated = { ...prev };
      if (updated[category][item]) {
        updated[category][item] = false;
      } else {
        updated[category][item] = true;
      }
      return updated;
    });
  };
  
  const handleToggleProduct = (productId) => {
    setAccessData(prev => {
      const updated = { ...prev };
      if (updated.products.includes(productId)) {
        updated.products = updated.products.filter(id => id !== productId);
      } else {
        updated.products = [...updated.products, productId];
      }
      return updated;
    });
  };
  
  const handleToggleLocation = (locationId) => {
    setAccessData(prev => {
      const updated = { ...prev };
      if (updated.locations.includes(locationId)) {
        updated.locations = updated.locations.filter(id => id !== locationId);
      } else {
        updated.locations = [...updated.locations, locationId];
      }
      return updated;
    });
  };
  
  const handleToggleApp = (appId) => {
    setAccessData(prev => {
      const updated = { ...prev };
      if (updated.apps.includes(appId)) {
        updated.apps = updated.apps.filter(id => id !== appId);
      } else {
        updated.apps = [...updated.apps, appId];
      }
      return updated;
    });
  };
  
  const handleSelectAllProducts = () => {
    setAccessData(prev => ({
      ...prev,
      products: products.map(p => p.id)
    }));
  };
  
  const handleSelectNoProducts = () => {
    setAccessData(prev => ({
      ...prev,
      products: []
    }));
  };
  
  const handleSelectAllLocations = () => {
    setAccessData(prev => ({
      ...prev,
      locations: locations.map(l => l.id)
    }));
  };
  
  const handleSelectNoLocations = () => {
    setAccessData(prev => ({
      ...prev,
      locations: []
    }));
  };
  
  const handleSelectAllApps = () => {
    setAccessData(prev => ({
      ...prev,
      apps: apps.map(a => a.id)
    }));
  };
  
  const handleSelectNoApps = () => {
    setAccessData(prev => ({
      ...prev,
      apps: []
    }));
  };
  
  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };
  
  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };
  
  const handleSave = () => {
    // Logic to save access permissions
    console.log('Saving access permissions:', accessData);
    onClose();
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit Access Permissions - ${user?.name || 'User'}`}>
      {/* Steps Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className={`flex items-center text-sm ${currentStep >= 1 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
            <span className={`h-6 w-6 flex items-center justify-center rounded-full ${currentStep >= 1 ? 'bg-blue-100' : 'bg-gray-100'} mr-2`}>1</span>
            Screen Access
          </div>
          <div className={`h-0.5 flex-1 mx-4 ${currentStep >= 2 ? 'bg-blue-400' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center text-sm ${currentStep >= 2 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
            <span className={`h-6 w-6 flex items-center justify-center rounded-full ${currentStep >= 2 ? 'bg-blue-100' : 'bg-gray-100'} mr-2`}>2</span>
            Products Access
          </div>
          <div className={`h-0.5 flex-1 mx-4 ${currentStep >= 3 ? 'bg-blue-400' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center text-sm ${currentStep >= 3 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
            <span className={`h-6 w-6 flex items-center justify-center rounded-full ${currentStep >= 3 ? 'bg-blue-100' : 'bg-gray-100'} mr-2`}>3</span>
            Locations Access
          </div>
          <div className={`h-0.5 flex-1 mx-4 ${currentStep >= 4 ? 'bg-blue-400' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center text-sm ${currentStep >= 4 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
            <span className={`h-6 w-6 flex items-center justify-center rounded-full ${currentStep >= 4 ? 'bg-blue-100' : 'bg-gray-100'} mr-2`}>4</span>
            Apps Access
          </div>
        </div>
      </div>
      
      {/* Step 1: Screen Access */}
      {currentStep === 1 && (
        <div>
          <h4 className="text-md font-medium mb-4">Screen & Feature Access</h4>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h5 className="text-sm font-medium mb-3">Screens</h5>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="dashboard-access" 
                      checked={accessData.screens.dashboard}
                      onChange={() => handleCheckboxChange('screens', 'dashboard')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="dashboard-access" className="ml-2 text-sm text-gray-700">
                      Dashboard
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="reviews-access" 
                      checked={accessData.screens.reviews}
                      onChange={() => handleCheckboxChange('screens', 'reviews')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="reviews-access" className="ml-2 text-sm text-gray-700">
                      Reviews
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="analytics-access" 
                      checked={accessData.screens.analytics}
                      onChange={() => handleCheckboxChange('screens', 'analytics')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="analytics-access" className="ml-2 text-sm text-gray-700">
                      Analytics
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="settings-access" 
                      checked={accessData.screens.settings}
                      onChange={() => handleCheckboxChange('screens', 'settings')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="settings-access" className="ml-2 text-sm text-gray-700">
                      Settings
                    </label>
                  </div>
                </div>
              </div>
              
              <div>
                <h5 className="text-sm font-medium mb-3">Actions</h5>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="view-action" 
                      checked={accessData.actions.view}
                      onChange={() => handleCheckboxChange('actions', 'view')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="view-action" className="ml-2 text-sm text-gray-700">
                      View
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="edit-action" 
                      checked={accessData.actions.edit}
                      onChange={() => handleCheckboxChange('actions', 'edit')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="edit-action" className="ml-2 text-sm text-gray-700">
                      Edit
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="delete-action" 
                      checked={accessData.actions.delete}
                      onChange={() => handleCheckboxChange('actions', 'delete')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="delete-action" className="ml-2 text-sm text-gray-700">
                      Delete
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="export-action" 
                      checked={accessData.actions.export}
                      onChange={() => handleCheckboxChange('actions', 'export')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    />
                    <label htmlFor="export-action" className="ml-2 text-sm text-gray-700">
                      Export Reports
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button 
              onClick={nextStep}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Next: Products Access
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Products Access */}
      {currentStep === 2 && (
        <div>
          <h4 className="text-md font-medium mb-4">Products Access</h4>
          
          <div className="flex justify-between mb-4">
            <div className="flex space-x-2">
              <button 
                onClick={handleSelectAllProducts}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
              >
                Select All
              </button>
              <button 
                onClick={handleSelectNoProducts}
                className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-md text-sm hover:bg-gray-100"
              >
                Select None
              </button>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-60 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              {products.map(product => (
                <div key={product.id} className="flex items-center p-2 bg-white rounded border border-gray-200">
                  <input 
                    type="checkbox" 
                    id={`product-${product.id}`} 
                    checked={accessData.products.includes(product.id)}
                    onChange={() => handleToggleProduct(product.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor={`product-${product.id}`} className="ml-2 flex-1">
                    <p className="text-sm font-medium">{product.name}</p>
                    <p className="text-xs text-gray-500">{product.category}</p>
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between">
            <button 
              onClick={prevStep}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
            >
              Back
            </button>
            <button 
              onClick={nextStep}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Next: Locations Access
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Locations Access */}
      {currentStep === 3 && (
        <div>
          <h4 className="text-md font-medium mb-4">Locations Access</h4>
          
          <div className="flex justify-between mb-4">
            <div className="flex space-x-2">
              <button 
                onClick={handleSelectAllLocations}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
              >
                Select All
              </button>
              <button 
                onClick={handleSelectNoLocations}
                className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-md text-sm hover:bg-gray-100"
              >
                Select None
              </button>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-60 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              {locations.map(location => (
                <div key={location.id} className="flex items-center p-2 bg-white rounded border border-gray-200">
                  <input 
                    type="checkbox" 
                    id={`location-${location.id}`} 
                    checked={accessData.locations.includes(location.id)}
                    onChange={() => handleToggleLocation(location.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor={`location-${location.id}`} className="ml-2 flex-1">
                    <p className="text-sm font-medium">{location.name}</p>
                    <p className="text-xs text-gray-500">{location.region} Region</p>
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between">
            <button 
              onClick={prevStep}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
            >
              Back
            </button>
            <button 
              onClick={nextStep}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Next: Apps Access
            </button>
          </div>
        </div>
      )}
      
      {/* Step 4: Apps Access */}
      {currentStep === 4 && (
        <div>
          <h4 className="text-md font-medium mb-4">Apps Access</h4>
          
          <div className="flex justify-between mb-4">
            <div className="flex space-x-2">
              <button 
                onClick={handleSelectAllApps}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
              >
                Select All
              </button>
              <button 
                onClick={handleSelectNoApps}
                className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-md text-sm hover:bg-gray-100"
              >
                Select None
              </button>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-2 gap-4">
              {apps.map(app => (
                <div key={app.id} className="flex items-center p-2 bg-white rounded border border-gray-200">
                  <input 
                    type="checkbox" 
                    id={`app-${app.id}`} 
                    checked={accessData.apps.includes(app.id)}
                    onChange={() => handleToggleApp(app.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor={`app-${app.id}`} className="ml-2 flex-1">
                    <p className="text-sm font-medium">{app.name}</p>
                    <p className="text-xs text-gray-500">{app.platform}</p>
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between">
            <button 
              onClick={prevStep}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
            >
              Back
            </button>
            <button 
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Save Access Permissions
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};


// Add New Product Modal
const AddProductModal = ({ isOpen, onClose }) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Add New Product">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Name
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter product name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select category</option>
                <option value="Water Purifiers">Water Purifiers</option>
                <option value="Water Filters">Water Filters</option>
                <option value="Water Solutions">Water Solutions</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter price"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-md font-medium mb-3">Marketplace Links</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amazon URL
                </label>
                <div className="flex">
                  <input 
                    type="text" 
                    className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://amazon.in/product-url"
                  />
                  <button className="bg-orange-100 text-orange-800 px-3 py-2 rounded-r-md border border-orange-200 text-sm font-medium">
                    Verify
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Flipkart URL
                </label>
                <div className="flex">
                  <input 
                    type="text" 
                    className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://flipkart.com/product-url"
                  />
                  <button className="bg-blue-100 text-blue-800 px-3 py-2 rounded-r-md border border-blue-200 text-sm font-medium">
                    Verify
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-md font-medium mb-3">Product Details</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-24"
                placeholder="Enter product description"
              ></textarea>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={onClose} 
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
            >
              Cancel
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              Add Product
            </button>
          </div>
        </div>
      </Modal>
    );
  };
  
  // Add New Location Modal
  const AddLocationModal = ({ isOpen, onClose }) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Add New Location">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Name
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter location name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Region
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select region</option>
                <option value="North">North</option>
                <option value="South">South</option>
                <option value="East">East</option>
                <option value="West">West</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-16"
                placeholder="Enter full address"
              ></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter city name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-md font-medium mb-3">Google Maps Location</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Google Maps URL
              </label>
              <div className="flex">
                <input 
                  type="text" 
                  className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://maps.google.com/?q=latitude,longitude"
                />
                <button className="bg-blue-100 text-blue-800 px-3 py-2 rounded-r-md border border-blue-200 text-sm font-medium">
                  Verify
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Format: https://maps.google.com/?q=latitude,longitude (e.g., https://maps.google.com/?q=28.6315,77.2167)
              </p>
            </div>
            
            <div className="mt-4 bg-gray-50 p-4 rounded-lg h-48 flex items-center justify-center border border-gray-200">
              <div className="text-center">
                <MapPin className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Map preview would appear here</p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={onClose} 
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
            >
              Cancel
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              Add Location
            </button>
          </div>
        </div>
      </Modal>
    );
  };
  
  // Add New App Modal
  const AddAppModal = ({ isOpen, onClose }) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Add New App">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                App Name
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter app name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Platform
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select platform</option>
                <option value="iOS & Android">iOS & Android</option>
                <option value="iOS">iOS</option>
                <option value="Android">Android</option>
                <option value="Web">Web</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Version
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 1.0.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="Active">Active</option>
                <option value="Beta">Beta</option>
                <option value="Deprecated">Deprecated</option>
              </select>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-md font-medium mb-3">App Store Links</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  iOS App Store URL
                </label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://apps.apple.com/in/app/your-app"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Play Store URL
                </label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://play.google.com/store/apps/details?id=com.your.app"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Web App URL
                </label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://yourwebapp.com"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={onClose} 
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
            >
              Cancel
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              Add App
            </button>
          </div>
        </div>
      </Modal>
    );
  };
  
  // Create New Role Modal
  const CreateRoleModal = ({ isOpen, onClose }) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Create New Role">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Name
            </label>
            <input 
              type="text" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter role name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-16"
              placeholder="Enter role description"
            ></textarea>
          </div>
          
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-md font-medium mb-3">Default Permissions</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="text-sm font-medium mb-3">Screen Access</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="dashboard-access" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor="dashboard-access" className="ml-2 text-sm text-gray-700">
                    Dashboard
                  </label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="reviews-access" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor="reviews-access" className="ml-2 text-sm text-gray-700">
                    Reviews
                  </label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="analytics-access" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor="analytics-access" className="ml-2 text-sm text-gray-700">
                    Analytics
                  </label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="settings-access" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor="settings-access" className="ml-2 text-sm text-gray-700">
                    Settings
                  </label>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium mb-3">Actions</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="view-action" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                    defaultChecked
                  />
                  <label htmlFor="view-action" className="ml-2 text-sm text-gray-700">
                    View
                  </label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="edit-action" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor="edit-action" className="ml-2 text-sm text-gray-700">
                    Edit
                  </label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="delete-action" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor="delete-action" className="ml-2 text-sm text-gray-700">
                    Delete
                  </label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="export-action" 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                  />
                  <label htmlFor="export-action" className="ml-2 text-sm text-gray-700">
                    Export Reports
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={onClose} 
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
            >
              Cancel
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              Create Role
            </button>
          </div>
        </div>
      </Modal>
    );
  };
  
  // Edit Role Modal
  const EditRoleModal = ({ isOpen, onClose, role }) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Edit Role - ${role?.name || ''}`}>
        {role && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role Name
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue={role.name}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-16"
                defaultValue={role.description}
              ></textarea>
            </div>
            
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-md font-medium mb-3">Permissions</h3>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium mb-3">Screen Access</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="dashboard-access-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked={role.name !== 'Reviewer'}
                    />
                    <label htmlFor="dashboard-access-edit" className="ml-2 text-sm text-gray-700">
                      Dashboard
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="reviews-access-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked
                    />
                    <label htmlFor="reviews-access-edit" className="ml-2 text-sm text-gray-700">
                      Reviews
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="analytics-access-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked={['Administrator', 'Manager', 'Analyst'].includes(role.name)}
                    />
                    <label htmlFor="analytics-access-edit" className="ml-2 text-sm text-gray-700">
                      Analytics
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="settings-access-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked={['Administrator', 'Manager'].includes(role.name)}
                    />
                    <label htmlFor="settings-access-edit" className="ml-2 text-sm text-gray-700">
                      Settings
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium mb-3">Actions</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="view-action-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked
                    />
                    <label htmlFor="view-action-edit" className="ml-2 text-sm text-gray-700">
                      View
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="edit-action-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked={['Administrator', 'Manager', 'Support Agent'].includes(role.name)}
                    />
                    <label htmlFor="edit-action-edit" className="ml-2 text-sm text-gray-700">
                      Edit
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="delete-action-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked={role.name === 'Administrator'}
                    />
                    <label htmlFor="delete-action-edit" className="ml-2 text-sm text-gray-700">
                      Delete
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="export-action-edit" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      defaultChecked={['Administrator', 'Manager', 'Analyst'].includes(role.name)}
                    />
                    <label htmlFor="export-action-edit" className="ml-2 text-sm text-gray-700">
                      Export Reports
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end pt-4">
              <button 
                onClick={onClose} 
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
              >
                Cancel
              </button>
              <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
                Update Role
              </button>
            </div>
          </div>
        )}
      </Modal>
    );
  };
  
  // Invite User Modal
  const InviteUserModal = ({ isOpen, onClose }) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Invite New User">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter last name"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input 
              type="email" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select role</option>
                <option value="Administrator">Administrator</option>
                <option value="Manager">Manager</option>
                <option value="Analyst">Analyst</option>
                <option value="Support Agent">Support Agent</option>
                <option value="Reviewer">Reviewer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select department</option>
                <option value="Product Management">Product Management</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
                <option value="Customer Support">Customer Support</option>
                <option value="Development">Development</option>
              </select>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center mb-4">
              <input 
                id="customize-access" 
                type="checkbox" 
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
              />
              <label htmlFor="customize-access" className="ml-2 text-sm text-gray-700">
                Customize access rights (optional)
              </label>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                If you'd like to customize access rights now, check the box above. Otherwise, default permissions for the selected role will be applied.
              </p>
              <p className="text-sm text-gray-600">
                You can always edit access permissions later from the Roles & Access settings.
              </p>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-700 mb-1">Invitation Message</h4>
              <p className="text-sm text-blue-600 mb-3">
                An invitation email will be sent to this user with instructions to set up their account.
              </p>
              <textarea 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-20"
                placeholder="Optional: Add a personal message to the invitation email"
              ></textarea>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={onClose} 
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
            >
              Cancel
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              Send Invitation
            </button>
          </div>
        </div>
      </Modal>
    );
  };
  
  // Edit Default Access Settings Modal
  const EditDefaultsModal = ({ isOpen, onClose }) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Edit Default Access Settings">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            Configure the default permissions for each role. These settings will apply to any new users assigned to these roles.
          </p>
          
          <div className="mb-6">
            <h3 className="text-md font-medium mb-3">Administrator Defaults</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="text-sm font-medium mb-2">Screen Access</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-dashboard" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-dashboard" className="ml-2 text-sm text-gray-700">
                        Dashboard
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-reviews" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-reviews" className="ml-2 text-sm text-gray-700">
                        Reviews
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-analytics" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-analytics" className="ml-2 text-sm text-gray-700">
                        Analytics
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-settings" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-settings" className="ml-2 text-sm text-gray-700">
                        Settings
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Actions</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-view" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-view" className="ml-2 text-sm text-gray-700">
                        View
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-edit" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-edit" className="ml-2 text-sm text-gray-700">
                        Edit
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-delete" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-delete" className="ml-2 text-sm text-gray-700">
                        Delete
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="admin-export" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="admin-export" className="ml-2 text-sm text-gray-700">
                        Export
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2">Data Access</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Products</label>
                    <select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option>All Products</option>
                      <option>Limited Products</option>
                      <option>No Access</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Locations</label>
                    <select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option>All Locations</option>
                      <option>Region-Based</option>
                      <option>Selected Locations</option>
                      <option>No Access</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Apps</label>
                    <select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option>All Apps</option>
                      <option>Selected Apps</option>
                      <option>No Access</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-md font-medium mb-3">Manager Defaults</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="text-sm font-medium mb-2">Screen Access</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-dashboard" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="manager-dashboard" className="ml-2 text-sm text-gray-700">
                        Dashboard
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-reviews" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="manager-reviews" className="ml-2 text-sm text-gray-700">
                        Reviews
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-analytics" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="manager-analytics" className="ml-2 text-sm text-gray-700">
                        Analytics
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-settings" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      />
                      <label htmlFor="manager-settings" className="ml-2 text-sm text-gray-700">
                        Settings
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Actions</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-view" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="manager-view" className="ml-2 text-sm text-gray-700">
                        View
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-edit" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="manager-edit" className="ml-2 text-sm text-gray-700">
                        Edit
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-delete" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                      />
                      <label htmlFor="manager-delete" className="ml-2 text-sm text-gray-700">
                        Delete
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="manager-export" 
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                        defaultChecked
                      />
                      <label htmlFor="manager-export" className="ml-2 text-sm text-gray-700">
                        Export
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2">Data Access</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Products</label>
                    <select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option>All Products</option>
                      <option selected>Limited Products</option>
                      <option>No Access</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Locations</label>
                    <select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option>All Locations</option>
                      <option selected>Region-Based</option>
                      <option>Selected Locations</option>
                      <option>No Access</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Apps</label>
                    <select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option>All Apps</option>
                      <option selected>Selected Apps</option>
                      <option>No Access</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={onClose} 
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
            >
              Cancel
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              Save Default Settings
            </button>
          </div>
        </div>
      </Modal>
    );
  };



// Brand Settings Components
const ProductSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Product data
  const products = [
    { id: 1, name: 'Product Original', category: 'Water Purifiers', price: '₹12,999', status: 'Active', amazonUrl: 'https://amazon.in/product-original', flipkartUrl: 'https://flipkart.com/product-original' },
    { id: 2, name: 'Product Premium', category: 'Water Purifiers', price: '₹16,999', status: 'Active', amazonUrl: 'https://amazon.in/product-premium', flipkartUrl: 'https://flipkart.com/product-premium' },
    { id: 3, name: 'Product Max', category: 'Water Purifiers', price: '₹19,999', status: 'Active', amazonUrl: 'https://amazon.in/product-max', flipkartUrl: '' },
    { id: 4, name: 'Product Everyday', category: 'Water Filters', price: '₹7,999', status: 'Active', amazonUrl: '', flipkartUrl: 'https://flipkart.com/product-everyday' },
    { id: 5, name: 'Product Complete', category: 'Water Solutions', price: '₹24,999', status: 'Inactive', amazonUrl: '', flipkartUrl: '' }
  ];

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setIsEditing(true);
  };

  const handleAddProduct = () => {
    setIsAddingNew(true);
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setIsAddingNew(false);
    setSelectedProduct(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Product Management</h2>
        <button 
          className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
          onClick={handleAddProduct}
        >
          Add New Product
        </button>
      </div>
      
      {/* Product table - as before */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Marketplace Links
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => (
              <tr key={product.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {product.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {product.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {product.price}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex flex-col">
                    <div className="flex items-center mb-1">
                      <span className="text-xs font-medium bg-orange-100 text-orange-800 px-1.5 rounded mr-2">Amazon</span>
                      {product.amazonUrl ? (
                        <a href={product.amazonUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                          <Link className="h-3 w-3 mr-1" />
                          <span>Link</span>
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">Not added</span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs font-medium bg-blue-100 text-blue-800 px-1.5 rounded mr-2">Flipkart</span>
                      {product.flipkartUrl ? (
                        <a href={product.flipkartUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                          <Link className="h-3 w-3 mr-1" />
                          <span>Link</span>
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">Not added</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    product.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {product.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button 
                    className="text-blue-600 hover:text-blue-800 mr-3"
                    onClick={() => handleEditProduct(product)}
                  >
                    Edit
                  </button>
                  <button className="text-red-600 hover:text-red-800">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Product Edit Modal - as before */}
      <Modal 
  isOpen={isEditing} 
  onClose={handleCloseModal}
  title={`Edit Product - ${selectedProduct?.name || ''}`}
>
  {selectedProduct && (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product Name
          </label>
          <input 
            type="text" 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            defaultValue={selectedProduct.name}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            <option value="Water Purifiers" selected={selectedProduct.category === 'Water Purifiers'}>Water Purifiers</option>
            <option value="Water Filters" selected={selectedProduct.category === 'Water Filters'}>Water Filters</option>
            <option value="Water Solutions" selected={selectedProduct.category === 'Water Solutions'}>Water Solutions</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Price
          </label>
          <input 
            type="text" 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            defaultValue={selectedProduct.price}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            <option value="Active" selected={selectedProduct.status === 'Active'}>Active</option>
            <option value="Inactive" selected={selectedProduct.status === 'Inactive'}>Inactive</option>
          </select>
        </div>
      </div>
      
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h3 className="text-md font-medium mb-3">Marketplace Links</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amazon URL
            </label>
            <div className="flex">
              <input 
                type="text" 
                className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue={selectedProduct.amazonUrl}
                placeholder="https://amazon.in/product-url"
              />
              <button className="bg-orange-100 text-orange-800 px-3 py-2 rounded-r-md border border-orange-200 text-sm font-medium">
                Verify
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Flipkart URL
            </label>
            <div className="flex">
              <input 
                type="text" 
                className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue={selectedProduct.flipkartUrl}
                placeholder="https://flipkart.com/product-url"
              />
              <button className="bg-blue-100 text-blue-800 px-3 py-2 rounded-r-md border border-blue-200 text-sm font-medium">
                Verify
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h3 className="text-md font-medium mb-3">Product Details</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-24"
            defaultValue="High-quality water purifier with advanced filtration technology. Suitable for all water sources."
          ></textarea>
        </div>
      </div>
      
      <div className="flex justify-end pt-4">
        <button 
          onClick={handleCloseModal} 
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
        >
          Cancel
        </button>
        <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
          Save Changes
        </button>
      </div>
    </div>
  )}
</Modal>
      
      {/* New Product Modal */}
      <AddProductModal 
        isOpen={isAddingNew}
        onClose={handleCloseModal}
      />

      <div className="mt-6 flex justify-end">
        <div className="bg-orange-50 border border-orange-200 rounded-md px-4 py-3 mr-3 flex items-start">
          <AlertTriangle className="h-5 w-5 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-orange-700">
            You don't have access to update product settings. Please contact your administrator for permission.
          </p>
        </div>
        <button disabled className="px-4 py-2 bg-gray-300 text-gray-500 rounded-md text-sm cursor-not-allowed">
          Save Changes
        </button>
      </div>
    </div>
  );
};

const LocationSettings = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    
    const locations = [
      { id: 1, name: 'Delhi Showroom', region: 'North', address: '24, Connaught Place, New Delhi', status: 'Active', mapUrl: 'https://maps.google.com/?q=28.6315,77.2167' },
      { id: 2, name: 'Mumbai Central', region: 'West', address: '15, Marine Drive, Mumbai', status: 'Active', mapUrl: 'https://maps.google.com/?q=18.9442,72.8239' },
      { id: 3, name: 'Bangalore Tech Park', region: 'South', address: '42, Whitefield, Bangalore', status: 'Active', mapUrl: 'https://maps.google.com/?q=12.9698,77.7500' },
      { id: 4, name: 'Kolkata East End', region: 'East', address: '8, Park Street, Kolkata', status: 'Inactive', mapUrl: 'https://maps.google.com/?q=22.5654,88.3512' },
      { id: 5, name: 'Chennai Beachside', region: 'South', address: '33, Marina Beach Road, Chennai', status: 'Active', mapUrl: 'https://maps.google.com/?q=13.0633,80.2433' }
    ];
  
    const handleEditLocation = (location) => {
      setSelectedLocation(location);
      setIsEditing(true);
    };
  
    const handleAddLocation = () => {
      setIsAddingNew(true);
    };
  
    const handleCloseModal = () => {
      setIsEditing(false);
      setIsAddingNew(false);
      setSelectedLocation(null);
    };
  
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Location Management</h2>
          <button 
            className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
            onClick={handleAddLocation}
          >
            Add New Location
          </button>
        </div>
        
        {/* Location table - as before */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            {/* Table content as before */}
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Region
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Google Maps
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {locations.map((location) => (
                <tr key={location.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {location.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {location.region}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {location.address}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {location.mapUrl ? (
                      <a href={location.mapUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        <span>View on Maps</span>
                      </a>
                    ) : (
                      <span className="text-gray-400 text-xs">Not added</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      location.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {location.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button 
                      className="text-blue-600 hover:text-blue-800 mr-3"
                      onClick={() => handleEditLocation(location)}
                    >
                      Edit
                    </button>
                    <button className="text-red-600 hover:text-red-800">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  
        {/* Location Edit Modal - as before */}
        {/* Location Edit Modal (Fixed) */}
<Modal 
  isOpen={isEditing} 
  onClose={handleCloseModal}
  title={`Edit Location - ${selectedLocation?.name || ''}`}
>
  {selectedLocation && (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location Name
          </label>
          <input 
            type="text" 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            defaultValue={selectedLocation.name}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Region
          </label>
          <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            <option value="North" selected={selectedLocation.region === 'North'}>North</option>
            <option value="South" selected={selectedLocation.region === 'South'}>South</option>
            <option value="East" selected={selectedLocation.region === 'East'}>East</option>
            <option value="West" selected={selectedLocation.region === 'West'}>West</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <textarea 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-16"
            defaultValue={selectedLocation.address}
          ></textarea>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <input 
            type="text" 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            defaultValue={selectedLocation.address.split(',').pop().trim()}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            <option value="Active" selected={selectedLocation.status === 'Active'}>Active</option>
            <option value="Inactive" selected={selectedLocation.status === 'Inactive'}>Inactive</option>
          </select>
        </div>
      </div>
      
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h3 className="text-md font-medium mb-3">Google Maps Location</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Google Maps URL
          </label>
          <div className="flex">
            <input 
              type="text" 
              className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              defaultValue={selectedLocation.mapUrl}
              placeholder="https://maps.google.com/?q=latitude,longitude"
            />
            <button className="bg-blue-100 text-blue-800 px-3 py-2 rounded-r-md border border-blue-200 text-sm font-medium">
              Verify
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Format: https://maps.google.com/?q=latitude,longitude (e.g., https://maps.google.com/?q=28.6315,77.2167)
          </p>
        </div>
        
        <div className="mt-4 bg-gray-50 p-4 rounded-lg h-48 flex items-center justify-center border border-gray-200">
          <div className="text-center">
            <MapPin className="h-8 w-8 text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Map preview would appear here</p>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end pt-4">
        <button 
          onClick={handleCloseModal} 
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
        >
          Cancel
        </button>
        <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
          Save Changes
        </button>
      </div>
    </div>
  )}
</Modal>
        
        {/* Add New Location Modal */}
        <AddLocationModal 
          isOpen={isAddingNew}
          onClose={handleCloseModal}
        />
  
        <div className="mt-6 flex justify-end">
          <div className="bg-orange-50 border border-orange-200 rounded-md px-4 py-3 mr-3 flex items-start">
            <AlertTriangle className="h-5 w-5 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-orange-700">
              You don't have access to update location settings. Please contact your administrator for permission.
            </p>
          </div>
          <button disabled className="px-4 py-2 bg-gray-300 text-gray-500 rounded-md text-sm cursor-not-allowed">
            Save Changes
          </button>
        </div>
      </div>
    );
  };
  

  const AppSettings = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
  
    const apps = [
    { id: 1, name: 'Product Mobile App', platform: 'iOS & Android', version: '2.3.1', status: 'Active', iosUrl: 'https://apps.apple.com/in/app/product', androidUrl: 'https://play.google.com/store/apps/details?id=com.product' },
    { id: 2, name: 'Product Service Portal', platform: 'Web', version: '1.8.0', status: 'Active', webUrl: 'https://service.product.com' },
    { id: 3, name: 'Product Agent App', platform: 'Android', version: '1.2.5', status: 'Active', androidUrl: 'https://play.google.com/store/apps/details?id=com.product.agent' }
    ];
  
    const handleEditApp = (app) => {
      setSelectedApp(app);
      setIsEditing(true);
    };
  
    const handleAddApp = () => {
      setIsAddingNew(true);
    };
  
    const handleCloseModal = () => {
      setIsEditing(false);
      setIsAddingNew(false);
      setSelectedApp(null);
    };
  
  return (
  <div>
  <div className="flex justify-between items-center mb-4">
  <h2 className="text-lg font-medium">App Management</h2>
  <button 
    className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
    onClick={handleAddApp}
  >
    Add New App
  </button>
  </div>
  
  {/* App table - as before */}
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
  <table className="min-w-full divide-y divide-gray-200">
    {/* Table content as before */}
    <thead className="bg-gray-50">
      <tr>
        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Name
        </th>
        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Platform
        </th>
        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Version
        </th>
        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          App Links
        </th>
        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Status
        </th>
        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Actions
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {apps.map((app) => (
        <tr key={app.id}>
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {app.name}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {app.platform}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {app.version}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            <div className="flex flex-col space-y-1">
              {app.iosUrl && (
                <a href={app.iosUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                  <Link className="h-3 w-3 mr-1" />
                  <span>iOS App Store</span>
                </a>
              )}
              {app.androidUrl && (
                <a href={app.androidUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                  <Link className="h-3 w-3 mr-1" />
                  <span>Google Play</span>
                </a>
              )}
              {app.webUrl && (
                <a href={app.webUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                  <Link className="h-3 w-3 mr-1" />
                  <span>Web App</span>
                </a>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              app.status === 'Active' ? 'bg-green-100 text-green-800' : 
              app.status === 'Beta' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {app.status}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            <button 
              className="text-blue-600 hover:text-blue-800 mr-3"
              onClick={() => handleEditApp(app)}
            >
              Edit
            </button>
            <button className="text-red-600 hover:text-red-800">Delete</button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
  </div>
  
  {/* App Edit Modal - as before */}
  <Modal 
  isOpen={isEditing} 
  onClose={handleCloseModal}
  title={`Edit App - ${selectedApp?.name || ''}`}
>
  {selectedApp && (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            App Name
          </label>
          <input 
            type="text" 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            defaultValue={selectedApp.name}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Platform
          </label>
          <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            <option value="iOS & Android" selected={selectedApp.platform === 'iOS & Android'}>iOS & Android</option>
            <option value="iOS" selected={selectedApp.platform === 'iOS'}>iOS</option>
            <option value="Android" selected={selectedApp.platform === 'Android'}>Android</option>
            <option value="Web" selected={selectedApp.platform === 'Web'}>Web</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Version
          </label>
          <input 
            type="text" 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            defaultValue={selectedApp.version}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            <option value="Active" selected={selectedApp.status === 'Active'}>Active</option>
            <option value="Beta" selected={selectedApp.status === 'Beta'}>Beta</option>
            <option value="Deprecated" selected={selectedApp.status === 'Deprecated'}>Deprecated</option>
          </select>
        </div>
      </div>
      
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h3 className="text-md font-medium mb-3">App Store Links</h3>
        <div className="space-y-4">
          {(selectedApp.platform === 'iOS' || selectedApp.platform === 'iOS & Android') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                iOS App Store URL
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue={selectedApp.iosUrl}
                placeholder="https://apps.apple.com/in/app/your-app"
              />
            </div>
          )}
          
          {(selectedApp.platform === 'Android' || selectedApp.platform === 'iOS & Android') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Google Play Store URL
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue={selectedApp.androidUrl}
                placeholder="https://play.google.com/store/apps/details?id=com.your.app"
              />
            </div>
          )}
          
          {selectedApp.platform === 'Web' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Web App URL
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue={selectedApp.webUrl}
                placeholder="https://yourwebapp.com"
              />
            </div>
          )}
        </div>
      </div>
      
      <div className="flex justify-end pt-4">
        <button 
          onClick={handleCloseModal} 
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 mr-2"
        >
          Cancel
        </button>
        <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
          Save Changes
        </button>
      </div>
    </div>
  )}
</Modal>
  
  {/* Add New App Modal */}
  <AddAppModal 
  isOpen={isAddingNew}
  onClose={handleCloseModal}
  />
  
  <div className="mt-6 flex justify-end">
  <div className="bg-orange-50 border border-orange-200 rounded-md px-4 py-3 mr-3 flex items-start">
    <AlertTriangle className="h-5 w-5 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-orange-700">
      You don't have access to update app settings. Please contact your administrator for permission.
    </p>
  </div>
  <button disabled className="px-4 py-2 bg-gray-300 text-gray-500 rounded-md text-sm cursor-not-allowed">
    Save Changes
  </button>
  </div>
  </div>
  );
  };

// Account Settings Components
const ProfileSettings = () => {
  return (
    <div>
      <h2 className="text-lg font-medium mb-4">Profile Settings</h2>
      
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start mb-6">
          <div className="mr-6">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-2xl font-bold">
              JS
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-md font-medium mb-1">John Smith</h3>
            <p className="text-sm text-gray-500 mb-3">Product Manager</p>
            <button className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100">
              Change Profile Picture
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input 
              type="text" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              defaultValue="John"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input 
              type="text" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              defaultValue="Smith"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input 
              type="email" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              defaultValue="john.smith@brandabc.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input 
              type="tel" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              defaultValue="+91 9876543210"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
              <option>Product Management</option>
              <option>Sales</option>
              <option>Marketing</option>
              <option>Customer Support</option>
              <option>Development</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Zone
            </label>
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
              <option>(GMT+05:30) India Standard Time</option>
              <option>(GMT+00:00) Greenwich Mean Time</option>
              <option>(GMT-05:00) Eastern Time</option>
              <option>(GMT-08:00) Pacific Time</option>
            </select>
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
            Save Profile
          </button>
        </div>
      </div>
    </div>
  );
};

const SecuritySettings = () => {
  return (
    <div>
      <h2 className="text-lg font-medium mb-4">Security Settings</h2>
      
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-md font-medium mb-3">Change Password</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input 
              type="password" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input 
              type="password" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter new password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input 
              type="password" 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Confirm new password"
            />
          </div>
          <div className="pt-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              Update Password
            </button>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-md font-medium mb-3">Two-Factor Authentication</h3>
        <p className="text-sm text-gray-600 mb-4">
          Add an extra layer of security to your account by enabling two-factor authentication.
        </p>
        
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
          <div>
            <h4 className="text-sm font-medium">SMS Authentication</h4>
            <p className="text-xs text-gray-500">Receive a code via SMS to verify your identity</p>
          </div>
          <div className="flex items-center">
            <span className="mr-3 text-sm text-green-600 font-medium">Enabled</span>
            <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
              Disable
            </button>
          </div>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <h4 className="text-sm font-medium">Authenticator App</h4>
            <p className="text-xs text-gray-500">Use an authenticator app to generate verification codes</p>
          </div>
          <div>
            <button className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100">
              Enable
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AccessSettings = () => {
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
    const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
    const [isEditDefaultsModalOpen, setIsEditDefaultsModalOpen] = useState(false);
    const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    
    const roles = [
    { id: 1, name: 'Administrator', users: 3, description: 'Full access to all settings and features' },
    { id: 2, name: 'Manager', users: 8, description: 'Can manage content and users, but cannot change system settings' },
    { id: 3, name: 'Analyst', users: 12, description: 'View-only access to analytics and reports' },
    { id: 4, name: 'Support Agent', users: 25, description: 'Can view and respond to customer reviews and feedback' },
    { id: 5, name: 'Reviewer', users: 5, description: 'Limited access to view specific content only' }
    ];
    
    const userList = [
    { id: 1, name: 'Priya Sharma', email: 'priya.sharma@brandabc.com', role: 'Administrator', status: 'Active' },
    { id: 2, name: 'Amit Kumar', email: 'amit.kumar@brandabc.com', role: 'Manager', status: 'Active' },
    { id: 3, name: 'Neha Singh', email: 'neha.singh@brandabc.com', role: 'Analyst', status: 'Active' },
    { id: 4, name: 'Raj Patel', email: 'raj.patel@brandabc.com', role: 'Support Agent', status: 'Inactive' },
    { id: 5, name: 'Sanjay Verma', email: 'sanjay.verma@brandabc.com', role: 'Reviewer', status: 'Active' }
    ];
    
    const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
    };
    
    const handleEditRole = (role) => {
    setSelectedRole(role);
    setIsEditRoleModalOpen(true);
    };
    
    const handleCreateRole = () => {
    setIsCreateRoleModalOpen(true);
    };
    
    const handleInviteUser = () => {
    setIsInviteUserModalOpen(true);
    };
    
    const handleEditDefaults = () => {
    setIsEditDefaultsModalOpen(true);
    };
    
    return (
    <div>
    <div className="flex justify-between items-center mb-4">
    <h2 className="text-lg font-medium">Role & Access Management</h2>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
    <div className="col-span-1">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-md font-medium mb-3">Roles</h3>
        <div className="space-y-3">
          {roles.map((role) => (
            <div key={role.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <div>
                <h4 className="text-sm font-medium">{role.name}</h4>
                <p className="text-xs text-gray-500">{role.users} users</p>
              </div>
              <button 
                className="text-blue-600 hover:text-blue-800 text-sm"
                onClick={() => handleEditRole(role)}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <button 
            className="w-full px-3 py-2 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
            onClick={handleCreateRole}
          >
            Create New Role
          </button>
        </div>
      </div>
    </div>
    
    <div className="col-span-2">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-md font-medium mb-3">User Access</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userList.map((user) => (
                <tr key={user.id}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
                        {user.name.split(' ').map(name => name[0]).join('')}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {user.role}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    <button 
                      className="text-blue-600 hover:text-blue-800 mr-3"
                      onClick={() => handleEditUser(user)}
                    >
                      Edit Access
                    </button>
                    <button className="text-red-600 hover:text-red-800">Deactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-between">
          <button 
            className="px-3 py-2 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
            onClick={handleInviteUser}
          >
            Invite New User
          </button>
          <button className="px-3 py-2 bg-gray-50 text-gray-600 rounded-md text-sm hover:bg-gray-100">
            View All Users
          </button>
        </div>
      </div>
    </div>
    </div>
    
    {/* Access Permissions Modal for editing user permissions */}
    <AccessPermissionsModal 
    isOpen={isUserModalOpen}
    onClose={() => setIsUserModalOpen(false)}
    user={selectedUser}
    />
    
    {/* Create New Role Modal */}
    <CreateRoleModal
    isOpen={isCreateRoleModalOpen}
    onClose={() => setIsCreateRoleModalOpen(false)}
    />
    
    {/* Edit Role Modal */}
    <EditRoleModal
    isOpen={isEditRoleModalOpen}
    onClose={() => setIsEditRoleModalOpen(false)}
    role={selectedRole}
    />
    
    {/* Invite User Modal */}
    <InviteUserModal
    isOpen={isInviteUserModalOpen}
    onClose={() => setIsInviteUserModalOpen(false)}
    />
    
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-md font-medium">Default Access Settings</h3>
      <button 
        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm hover:bg-blue-100"
        onClick={handleEditDefaults}
      >
        Edit Defaults
      </button>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium mb-2">Administrator</h4>
        <ul className="text-xs text-gray-600 space-y-1 pl-4 list-disc">
          <li>Full access to all screens</li>
          <li>Can modify system settings</li>
          <li>Can manage all users and roles</li>
          <li>Access to all products, locations, and apps</li>
        </ul>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium mb-2">Manager</h4>
        <ul className="text-xs text-gray-600 space-y-1 pl-4 list-disc">
          <li>Access to dashboard, reviews, and analytics</li>
          <li>Limited access to settings</li>
          <li>Can manage specific users</li>
          <li>Access limited to assigned locations</li>
        </ul>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium mb-2">Support Agent</h4>
        <ul className="text-xs text-gray-600 space-y-1 pl-4 list-disc">
          <li>Access to dashboard and reviews</li>
          <li>No access to settings</li>
          <li>Can reply to customer reviews</li>
          <li>Access limited to assigned products</li>
        </ul>
      </div>
    </div>
    </div>
    
    {/* Edit Default Access Settings Modal */}
    <EditDefaultsModal
    isOpen={isEditDefaultsModalOpen}
    onClose={() => setIsEditDefaultsModalOpen(false)}
    />
    
    <div className="flex justify-end">
    <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
      Save Access Settings
    </button>
    </div>
    </div>
    );
    };

const NotificationSettings = () => {
  return (
    <div>
      <h2 className="text-lg font-medium mb-4">Notification Settings</h2>
      
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-md font-medium mb-4">Email Notifications</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div>
              <h4 className="text-sm font-medium">New Reviews</h4>
              <p className="text-xs text-gray-500">Receive emails when new reviews are submitted</p>
            </div>
            <div className="flex items-center">
              <select className="mr-3 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option>Daily Digest</option>
                <option>Weekly Digest</option>
              </select>
              <div className="relative inline-block w-10 mr-2 align-middle select-none">
                <input type="checkbox" id="new-reviews" className="sr-only" defaultChecked />
                <div className="block bg-gray-200 w-10 h-5 rounded-full"></div>
                <div className="dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition"></div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div>
              <h4 className="text-sm font-medium">Negative Reviews</h4>
              <p className="text-xs text-gray-500">Get notified about negative reviews that require attention</p>
            </div>
            <div className="flex items-center">
              <select className="mr-3 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option>Daily Digest</option>
                <option>Weekly Digest</option>
              </select>
              <div className="relative inline-block w-10 mr-2 align-middle select-none">
                <input type="checkbox" id="negative-reviews" className="sr-only" defaultChecked />
                <div className="block bg-gray-200 w-10 h-5 rounded-full"></div>
                <div className="dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition"></div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div>
              <h4 className="text-sm font-medium">Analytics Reports</h4>
              <p className="text-xs text-gray-500">Receive regular analytics and performance reports</p>
            </div>
            <div className="flex items-center">
              <select className="mr-3 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option>Weekly</option>
                <option>Monthly</option>
              </select>
              <div className="relative inline-block w-10 mr-2 align-middle select-none">
                <input type="checkbox" id="analytics-reports" className="sr-only" defaultChecked />
                <div className="block bg-gray-200 w-10 h-5 rounded-full"></div>
                <div className="dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition"></div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium">System Notifications</h4>
              <p className="text-xs text-gray-500">Updates about the system, maintenance, and new features</p>
            </div>
            <div className="flex items-center">
              <div className="relative inline-block w-10 mr-2 align-middle select-none">
                <input type="checkbox" id="system-notifications" className="sr-only" defaultChecked />
                <div className="block bg-gray-200 w-10 h-5 rounded-full"></div>
                <div className="dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-md font-medium mb-4">In-App Notifications</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div>
              <h4 className="text-sm font-medium">Review Alerts</h4>
              <p className="text-xs text-gray-500">Show notifications for new and important reviews</p>
            </div>
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input type="checkbox" id="review-alerts" className="sr-only" defaultChecked />
              <div className="block bg-gray-200 w-10 h-5 rounded-full"></div>
              <div className="dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition"></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div>
              <h4 className="text-sm font-medium">Task Reminders</h4>
              <p className="text-xs text-gray-500">Get reminders about pending tasks and actions</p>
            </div>
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input type="checkbox" id="task-reminders" className="sr-only" defaultChecked />
              <div className="block bg-gray-200 w-10 h-5 rounded-full"></div>
              <div className="dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition"></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium">App Updates</h4>
              <p className="text-xs text-gray-500">Updates about app integrations and syncs</p>
            </div>
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input type="checkbox" id="marketplace-updates" className="sr-only" defaultChecked />
              <div className="block bg-gray-200 w-10 h-5 rounded-full"></div>
              <div className="dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition"></div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
            Save Notification Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Settings Component
const Settings = () => {
    const [activeTab, setActiveTab] = useState('profile');
    
    // Tab configuration to easily manage tabs
    const tabs = {
    brand: [
    { id: 'products', label: 'Products', icon: <Tag className="h-4 w-4" /> },
    { id: 'locations', label: 'Locations', icon: <MapPin className="h-4 w-4" /> },
    { id: 'apps', label: 'Apps', icon: <Smartphone className="h-4 w-4" /> }
    ],
    account: [
    { id: 'profile', label: 'Profile', icon: <UserCog className="h-4 w-4" /> },
    { id: 'security', label: 'Security', icon: <Cog className="h-4 w-4" /> },
    { id: 'access', label: 'Roles & Access', icon: <Building className="h-4 w-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <AlertTriangle className="h-4 w-4" /> }
    ]
    };
    
    // Render the active tab content
    const renderTabContent = () => {
    switch (activeTab) {
    case 'products':
    return <ProductSettings />;
    case 'locations':
    return <LocationSettings />;
    case 'apps':
    return <AppSettings />;
    case 'profile':
    return <ProfileSettings />;
    case 'security':
    return <SecuritySettings />;
    case 'access':
    return <AccessSettings />;
    case 'notifications':
    return <NotificationSettings />;
    default:
    return <ProfileSettings />;
    }
    };
    
    return (
    <div>
    <div className="mb-6">
    <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
    <p className="text-sm text-gray-600">
      Configure your brand and account settings
    </p>
    </div>
    
    {/* Main tabs for Brand vs Account Settings */}
    <div className="mb-6 flex border-b border-gray-200">
    <button
      className={`px-6 py-3 text-sm font-medium ${
        tabs.account.some(tab => tab.id === activeTab)
          ? 'text-blue-600 border-b-2 border-blue-600'
          : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
      onClick={() => setActiveTab('profile')}
    >
      Account Settings
    </button>
    <button
      className={`px-6 py-3 text-sm font-medium ${
        tabs.brand.some(tab => tab.id === activeTab)
          ? 'text-blue-600 border-b-2 border-blue-600'
          : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
      onClick={() => setActiveTab('products')}
    >
      Brand Settings
    </button>
   
    </div>
    
    {/* Sub-tabs */}
    <div className="flex flex-wrap border-b border-gray-200 mb-6">
    {/* Brand settings tabs */}
    {tabs.brand.some(tab => tab.id === activeTab) && 
      tabs.brand.map(tab => (
        <TabButton
          key={tab.id}
          active={activeTab === tab.id}
          icon={tab.icon}
          label={tab.label}
          onClick={() => setActiveTab(tab.id)}
        />
      ))
    }
    
    {/* Account settings tabs */}
    {tabs.account.some(tab => tab.id === activeTab) && 
      tabs.account.map(tab => (
        <TabButton
          key={tab.id}
          active={activeTab === tab.id}
          icon={tab.icon}
          label={tab.label}
          onClick={() => setActiveTab(tab.id)}
        />
      ))
    }
    </div>
    
    {/* Tab content */}
    <div>
    {renderTabContent()}
    </div>
    </div>
    );
    };
    
export default Settings;